import { useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import {
  VibesitesImage,
  getMediaUrl,
  parseStorageKey,
  videoThumbStorageKey,
} from '@vibesites/ui-primitives';

/**
 * Background Media for the hero. A single slot group so the editor can swap
 * image and/or video in one place. When a video URL is present it plays as
 * the background (muted / looping / poster-first); otherwise the image is
 * used. Decorative — page heading lives in PropertyOverview — so we don't
 * expose an alt field.
 */
const backgroundDef: SlotGroupDefinition = {
  label: 'Background Media',
  description: 'If a video is set, it plays as the background. Otherwise the image is used.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    video: {
      label: 'Video',
      type: 'video',
      defaultField: 'property_featured_video',
    },
  },
};

export function Hero() {
  const bg = useSlotGroup('hero.background', backgroundDef);

  // Prefer the bound video when present; otherwise use the image.
  const showVideo = !!bg.video;

  // Derive the poster from the *bound* video's storage key so it stays
  // correct even when the operator overrides the slot to a different clip.
  // Falls back to the image field when the video has no thumb yet.
  const thumbKey = videoThumbStorageKey(parseStorageKey(bg.video || null));
  const poster =
    (thumbKey ? getMediaUrl(thumbKey, { width: 1920 }) : null) || bg.image || '';

  // Respect prefers-reduced-motion: drop the autoplay attribute (pause()
  // alone loses the race if the video hasn't buffered yet) and pause anything
  // already playing. Leaves the poster frame showing.
  const videoRef = useRef<HTMLVideoElement>(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      setReducedMotion(mq.matches);
      if (mq.matches) videoRef.current?.pause();
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  return (
    <section className="hero-boxed relative w-full bg-[var(--vibe-color-background)] px-3 sm:px-5 md:px-6 pt-4 sm:pt-5 md:pt-6 pb-0">
      <div className="hero-box relative w-full overflow-hidden">
        {/* Background media. A bound video (if present) wins over the image. */}
        <EditableSlotGroup
          groupPath="hero.background"
          definition={backgroundDef}
          className="absolute inset-0 z-0"
        >
          <div className="absolute inset-0">
            {showVideo ? (
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                src={bg.video || undefined}
                poster={poster}
                autoPlay={!reducedMotion}
                muted
                loop
                playsInline
                aria-hidden
              />
            ) : (
              <VibesitesImage
                url={bg.image || ''}
                alt=""
                priority
                sizes="100vw"
                className="w-full h-full object-cover"
              />
            )}
          </div>
        </EditableSlotGroup>
      </div>

      <style>{`
        /* A full viewport tall at every breakpoint, and deliberately free of
           --offer-banner-height: the banner shifts the hero down via the
           header spacer but must never change how tall the video renders.
           svh (not dvh) so mobile browser chrome can't crop the frame or
           resize it mid-scroll. */
        .hero-box {
          height: 100vh;
          height: 100svh;
          background: #000;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  slotGroups: {
    'hero.background': backgroundDef,
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
